import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { completeShopOrder } from "@/lib/shop.functions";
import { downloadFile } from "@/lib/download-file";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, Loader2 } from "lucide-react";

export const Route = createFileRoute("/shop/success")({
  validateSearch: (search: Record<string, unknown>) =>
    z.object({ session_id: z.string().optional() }).parse(search),
  head: () => ({
    meta: [
      { title: "Your download — Recovery Trauma™ Shop" },
      {
        name: "description",
        content: "Thank you for your purchase. Your Recovery Trauma™ download is ready.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ShopSuccessPage,
});

function ShopSuccessPage() {
  const { session_id: sessionId } = Route.useSearch();
  const complete = useServerFn(completeShopOrder);
  const [state, setState] = useState<"loading" | "ready" | "pending" | "error">("loading");
  const [title, setTitle] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setMessage("We couldn't find this order.");
      return;
    }
    let stop = false;
    async function run() {
      const res = await complete({ data: { session_id: sessionId! } });
      if (stop) return;
      if ("error" in res && res.error) {
        setState("error");
        setMessage(res.error);
        return;
      }
      if (res.status === "pending") {
        tries.current += 1;
        if (tries.current > 10) {
          setState("pending");
          return;
        }
        setTimeout(run, 2000);
        return;
      }
      setTitle(res.title ?? null);
      setUrl(res.downloadUrl ?? null);
      setState("ready");
    }
    void run();
    return () => {
      stop = true;
    };
  }, [sessionId, complete]);

  return (
    <main className="mx-auto w-full max-w-xl px-4 py-16 text-center">
      {state === "loading" ? (
        <>
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" aria-hidden="true" />
          <p className="mt-4 text-muted-foreground">Confirming your payment…</p>
        </>
      ) : state === "ready" ? (
        <>
          <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
          <h1 className="mt-4 text-2xl font-semibold">Thank you</h1>
          <p className="mt-2 text-muted-foreground">
            {title ? `${title} is ready for you.` : "Your purchase is complete."}
          </p>
          {url ? (
            <Button className="mt-6" onClick={() => void downloadFile(url, title ?? undefined)}>
              <Download className="me-2 h-4 w-4" aria-hidden="true" /> Download now
            </Button>
          ) : (
            <p className="mt-6 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
              Your file is being prepared. We'll email it to you shortly — or contact support and
              we'll send it straight away.
            </p>
          )}
          <p className="mt-6 text-sm text-muted-foreground">
            You can find this again any time on your{" "}
            <Link to="/shop/downloads" className="text-primary underline">
              downloads page
            </Link>
            .
          </p>
        </>
      ) : state === "pending" ? (
        <>
          <h1 className="text-2xl font-semibold">Payment still processing</h1>
          <p className="mt-2 text-muted-foreground">
            Your payment hasn't confirmed yet. Refresh this page in a moment, or check your
            account page shortly.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="mt-2 text-muted-foreground">{message}</p>
          <Link to="/shop" className="mt-6 inline-block text-primary underline">
            Back to the shop
          </Link>
        </>
      )}
    </main>
  );
}
